import { useState } from 'react';

import { LocaleLink as Link } from '../lib/locale-link.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useI18n } from '../lib/use-i18n.js';
import { agentLoginUrl } from '../features/signup/redirect-to-dashboard.js';
import { computeAgentEarnings } from '../lib/agent-earnings.js';
import { defaultCommissionRules, defaultPlanId, defaultPlans } from '@kilat/config-seed';
import { money } from '@kilat/shared';
import type { AgentTier } from '@kilat/shared';

/**
 * /become-an-agent — sales agent acquisition landing.
 *
 * Sprint 89: routed through `useI18n('marketing.agent.*')` so the
 * landing pitch flips with the language switcher. Brand name still
 * comes from `usePlatformBrand()` for the back link + eyebrow chip.
 */
export function BecomeAnAgentPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  useMarketingSeo({
    title: t('marketing.agent.metaTitle', { brand: brand.name }),
    description: t('marketing.agent.metaDescription', { brand: brand.name }),
  });

  return (
    <main
      className="min-h-dvh flex flex-col"
      style={{ background: 'var(--color-surface)', color: 'var(--color-on-surface)' }}
    >
      <header
        style={{
          padding: 'var(--space-5) var(--space-5) 0 var(--space-5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 'var(--touch-target, 44px)',
            color: 'var(--color-on-surface-muted)',
            fontSize: 'var(--text-meta)',
            textDecoration: 'none',
          }}
        >
          {'← '}
          {brand.shortName}
        </Link>
        {/* Returning-agent sign-in — DIRECT link to the agent PWA origin
            (env.agentOrigin). No combined chooser page (owner amendment). */}
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {t('marketing.agent.signInPrompt', { brand: brand.shortName })}{' '}
          <a
            href={agentLoginUrl()}
            style={{
              color: 'var(--color-primary)',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            {t('marketing.agent.signInCta')}
          </a>
        </span>
      </header>

      <section
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-12) var(--space-6)',
          textAlign: 'center',
        }}
      >
        <p
          aria-hidden
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            letterSpacing: 4,
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          {brand.name} · {t('marketing.agent.eyebrow')}
        </p>
        <h1
          style={{
            fontSize: 'var(--text-display)',
            lineHeight: 1.05,
            fontWeight: 600,
            margin: 'var(--space-4) 0 0 0',
            maxWidth: 720,
          }}
        >
          {t('marketing.agent.headline')}
        </h1>
        <p
          style={{
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            marginTop: 'var(--space-4)',
            maxWidth: 560,
            lineHeight: 1.5,
          }}
        >
          {t('marketing.agent.sub', { brand: brand.name })}
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
            gap: 'var(--space-4)',
            marginTop: 'var(--space-8)',
            maxWidth: 720,
            width: '100%',
          }}
        >
          <Pillar
            title={t('marketing.agent.pillar1Title')}
            body={t('marketing.agent.pillar1Body')}
          />
          <Pillar
            title={t('marketing.agent.pillar2Title')}
            body={t('marketing.agent.pillar2Body')}
          />
          <Pillar
            title={t('marketing.agent.pillar3Title')}
            body={t('marketing.agent.pillar3Body')}
          />
        </div>

        <div
          style={{
            marginTop: 'var(--space-10)',
            display: 'flex',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <Link
            to="/become-an-agent/signup"
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              padding: '14px 28px',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-body)',
              fontWeight: 500,
              textDecoration: 'none',
              boxShadow: 'var(--shadow-2)',
            }}
          >
            {t('marketing.agent.ctaApply')}
          </Link>
          <Link
            to="/"
            style={{
              padding: '14px 28px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border-strong)',
              color: 'var(--color-on-surface)',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            {t('marketing.agent.altCta')}
          </Link>
        </div>

        <p
          style={{
            marginTop: 'var(--space-8)',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            maxWidth: 480,
          }}
        >
          {t('marketing.agent.kycFootnote')}
        </p>
      </section>

      <EarningsCalculator />
    </main>
  );
}

function Pillar({ title, body }: { readonly title: string; readonly body: string }) {
  return (
    <article
      style={{
        padding: 'var(--space-5) var(--space-4)',
        background: 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        textAlign: 'left',
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>{title}</p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
    </article>
  );
}

const CALC_MIN_OPERATORS = 1;
const CALC_MAX_OPERATORS = 30;
const CALC_INITIAL_OPERATORS = 6;
const CUSTOM_PLAN = 'custom';

const TIER_KEY: Record<AgentTier, string> = {
  bronze: 'marketing.becomeAgent.calc.tierBronze',
  silver: 'marketing.becomeAgent.calc.tierSilver',
  gold: 'marketing.becomeAgent.calc.tierGold',
};

/**
 * Interactive one-time-commission estimator. Everything it shows is a single
 * payout per operator (never a repeating stream): it runs the same
 * `computeAgentSignupCommission` math the production accrual trigger uses, then
 * walks the Bronze → Silver → Gold auto-promotion ladder as signups accrue.
 * Renders a deterministic default on first paint so prerender/hydration match.
 */
function EarningsCalculator() {
  const { t } = useI18n();
  const rules = defaultCommissionRules;
  const [operators, setOperators] = useState(CALC_INITIAL_OPERATORS);
  const [selection, setSelection] = useState<string>(defaultPlanId);
  const [customRm, setCustomRm] = useState('');

  const fallbackPlan = defaultPlans.find((p) => p.id === defaultPlanId) ?? defaultPlans[0];
  const fallbackSen = fallbackPlan?.basePrice ?? 0;
  const selectedPlan = defaultPlans.find((p) => p.id === selection);
  const parsedCustom = Number.parseFloat(customRm);
  const customSen =
    Number.isFinite(parsedCustom) && parsedCustom > 0
      ? Math.round(parsedCustom * 100)
      : fallbackSen;
  const monthlySubscriptionSen = selectedPlan ? selectedPlan.basePrice : customSen;

  const { totalSen, tierBreakdown } = computeAgentEarnings({
    operatorCount: operators,
    monthlySubscriptionSen,
    rules,
  });

  const silverThreshold = rules.autoPromotionThresholds.silverActiveOperators;
  const goldThreshold = rules.autoPromotionThresholds.goldActiveOperators;
  const silverMult = String(rules.tierMultipliers.silver ?? 1.1);
  const goldMult = String(rules.tierMultipliers.gold ?? 1.25);

  const currentTier: AgentTier =
    operators >= goldThreshold ? 'gold' : operators >= silverThreshold ? 'silver' : 'bronze';

  const milestone =
    operators < silverThreshold
      ? t('marketing.becomeAgent.calc.nextSilver', {
          remaining: silverThreshold - operators,
          mult: silverMult,
        })
      : operators < goldThreshold
        ? t('marketing.becomeAgent.calc.nextGold', {
            remaining: goldThreshold - operators,
            mult: goldMult,
          })
        : t('marketing.becomeAgent.calc.atGold', { mult: goldMult });

  const ladder: readonly { tier: AgentTier; caption: string; reached: boolean }[] = [
    {
      tier: 'bronze',
      caption: t('marketing.becomeAgent.calc.ladderBronzeCaption'),
      reached: true,
    },
    {
      tier: 'silver',
      caption: t('marketing.becomeAgent.calc.ladderSilverCaption', {
        count: silverThreshold,
        mult: silverMult,
      }),
      reached: operators >= silverThreshold,
    },
    {
      tier: 'gold',
      caption: t('marketing.becomeAgent.calc.ladderGoldCaption', {
        count: goldThreshold,
        mult: goldMult,
      }),
      reached: operators >= goldThreshold,
    },
  ];

  const activeRows = tierBreakdown.filter((row) => row.count > 0);

  return (
    <section
      style={{ padding: 'var(--space-12) var(--space-6)', background: 'var(--tint-subtle)' }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
        <p style={calcEyebrowStyle}>{t('marketing.becomeAgent.calc.eyebrow')}</p>
        <h2
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-h1)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {t('marketing.becomeAgent.calc.title')}
        </h2>
        <p
          style={{
            margin: 'var(--space-3) auto 0 auto',
            maxWidth: 540,
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('marketing.becomeAgent.calc.subtitle')}
        </p>

        <div
          style={{
            marginTop: 'var(--space-8)',
            padding: 'var(--space-6)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-1)',
            textAlign: 'left',
          }}
        >
          {/* Operators slider */}
          <label style={{ display: 'block' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 'var(--space-2)',
              }}
            >
              <span style={fieldLabelStyle}>{t('marketing.becomeAgent.calc.operatorsLabel')}</span>
              <span
                style={{
                  fontSize: 'var(--text-h2)',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {operators}
              </span>
            </div>
            <input
              type="range"
              min={CALC_MIN_OPERATORS}
              max={CALC_MAX_OPERATORS}
              step={1}
              value={operators}
              onChange={(e) => setOperators(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--color-primary)' }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                marginTop: 'var(--space-1)',
              }}
            >
              <span>{CALC_MIN_OPERATORS}</span>
              <span>{CALC_MAX_OPERATORS}</span>
            </div>
          </label>

          {/* Plan / subscription picker */}
          <fieldset
            style={{
              border: 0,
              padding: 0,
              margin: 'var(--space-6) 0 0 0',
              minInlineSize: 'auto',
            }}
          >
            <legend style={{ ...fieldLabelStyle, padding: 0, marginBottom: 'var(--space-2)' }}>
              {t('marketing.becomeAgent.calc.planLabel')}
            </legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {defaultPlans.map((plan) => (
                <PlanChip
                  key={plan.id}
                  selected={selection === plan.id}
                  onSelect={() => setSelection(plan.id)}
                  label={plan.name}
                  sub={money.format(plan.basePrice)}
                />
              ))}
              <PlanChip
                selected={selection === CUSTOM_PLAN}
                onSelect={() => setSelection(CUSTOM_PLAN)}
                label={t('marketing.becomeAgent.calc.customChip')}
              />
            </div>
            {selection === CUSTOM_PLAN ? (
              <div
                style={{
                  marginTop: 'var(--space-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  maxWidth: 240,
                }}
              >
                <span style={{ color: 'var(--color-on-surface-muted)', fontWeight: 600 }}>
                  {t('marketing.becomeAgent.calc.currencyPrefix')}
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={1}
                  value={customRm}
                  onChange={(e) => setCustomRm(e.target.value)}
                  aria-label={t('marketing.becomeAgent.calc.customInputLabel')}
                  placeholder={t('marketing.becomeAgent.calc.customInputPlaceholder')}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border-strong)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-on-surface)',
                    fontSize: 'var(--text-body)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                />
              </div>
            ) : null}
          </fieldset>

          {/* Result */}
          <div
            aria-live="polite"
            aria-atomic="true"
            style={{
              marginTop: 'var(--space-6)',
              paddingTop: 'var(--space-6)',
              borderTop: '1px solid var(--color-border)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--space-2)',
                flexWrap: 'wrap',
              }}
            >
              <span style={fieldLabelStyle}>{t('marketing.becomeAgent.calc.resultLabel')}</span>
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                  background: 'var(--color-primary-container, var(--color-border))',
                  borderRadius: 'var(--radius-pill)',
                  padding: '2px 10px',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                {t('marketing.becomeAgent.calc.oneTimeBadge')}
              </span>
            </div>
            <p
              style={{
                margin: 'var(--space-2) 0 0 0',
                fontSize: 'var(--text-display)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.05,
              }}
            >
              {money.format(totalSen)}
            </p>
            <p
              style={{
                margin: 'var(--space-3) auto 0 auto',
                maxWidth: 460,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                lineHeight: 1.5,
              }}
            >
              {t('marketing.becomeAgent.calc.resultCaption')}
            </p>
          </div>

          {/* How it stacks up */}
          {activeRows.length > 0 ? (
            <div
              style={{
                marginTop: 'var(--space-6)',
                paddingTop: 'var(--space-4)',
                borderTop: '1px solid var(--color-border)',
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              <p style={{ ...fieldLabelStyle, margin: 0 }}>
                {t('marketing.becomeAgent.calc.breakdownTitle')}
              </p>
              {activeRows.map((row) => (
                <div
                  key={row.tier}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 'var(--space-3)',
                    fontSize: 'var(--text-meta)',
                    color: 'var(--color-on-surface-muted)',
                  }}
                >
                  <span>
                    {t('marketing.becomeAgent.calc.tierRowLabel', {
                      tier: t(TIER_KEY[row.tier]),
                      count: row.count,
                      per: money.format(row.perSignupSen),
                    })}
                  </span>
                  <span
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      color: 'var(--color-on-surface)',
                      fontWeight: 600,
                    }}
                  >
                    {money.format(row.subtotalSen)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {/* Tier ladder */}
          <div
            style={{
              marginTop: 'var(--space-6)',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--color-border)',
            }}
          >
            <p style={{ ...fieldLabelStyle, margin: '0 0 var(--space-3) 0' }}>
              {t('marketing.becomeAgent.calc.ladderTitle')}
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
                gap: 'var(--space-2)',
              }}
            >
              {ladder.map((step) => {
                const isCurrent = step.tier === currentTier;
                return (
                  <div
                    key={step.tier}
                    aria-current={isCurrent ? 'step' : undefined}
                    style={{
                      padding: 'var(--space-3) var(--space-4)',
                      borderRadius: 'var(--radius-md)',
                      border: isCurrent
                        ? '2px solid var(--color-primary)'
                        : '1px solid var(--color-border-strong)',
                      background: step.reached ? 'var(--tint-subtle)' : 'transparent',
                      opacity: step.reached ? 1 : 0.55,
                    }}
                  >
                    <p style={{ margin: 0, fontWeight: 700 }}>{t(TIER_KEY[step.tier])}</p>
                    <p
                      style={{
                        margin: 'var(--space-1) 0 0 0',
                        fontSize: 'var(--text-meta)',
                        color: 'var(--color-on-surface-muted)',
                        lineHeight: 1.4,
                      }}
                    >
                      {step.caption}
                    </p>
                  </div>
                );
              })}
            </div>
            <p
              style={{
                margin: 'var(--space-3) 0 0 0',
                fontSize: 'var(--text-body)',
                color: 'var(--color-on-surface)',
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              {milestone}
            </p>
          </div>
        </div>

        <p
          style={{
            margin: 'var(--space-4) auto 0 auto',
            maxWidth: 560,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('marketing.becomeAgent.calc.disclaimer')}
        </p>

        <Link
          to="/become-an-agent/signup"
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-5)',
            padding: '12px 26px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            fontWeight: 600,
            textDecoration: 'none',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {t('marketing.becomeAgent.calc.cta')}
        </Link>
      </div>
    </section>
  );
}

function PlanChip({
  selected,
  onSelect,
  label,
  sub,
}: {
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly label: string;
  readonly sub?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        padding: '10px 16px',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        border: selected
          ? '2px solid var(--color-primary)'
          : '1px solid var(--color-border-strong)',
        background: selected ? 'var(--tint-subtle)' : 'var(--color-surface)',
        color: 'var(--color-on-surface)',
        font: 'inherit',
        minHeight: 'var(--touch-target, 44px)',
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 'var(--text-body)' }}>{label}</span>
      {sub ? (
        <span
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {sub}
        </span>
      ) : null}
    </button>
  );
}

const calcEyebrowStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
  letterSpacing: 3,
  textTransform: 'uppercase',
  fontWeight: 600,
  margin: 0,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};
